import type { FeatureLike } from 'ol/Feature';
import { Circle, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { getLength } from 'ol/sphere';
import type { Projection } from 'ol/proj';

import { CANVAS_COLOR_VARS, getCanvasColor } from '../../canvas-theme.utils';

/** ui-canvas `markerIcons` — served under `{CANVAS_ICONS_BASE_URL}/marker/`. */
export const CANVAS_MARKER_ICON_FILES = [
  'marker_red.svg',
  'marker_green.svg',
  'marker_blue.svg',
  'marker_orange.svg',
  'marker_purple.svg',
  'plane.svg',
  'hospital.svg',
  'fire.svg',
  'train_1.svg',
  'train_2.svg',
  'school.svg',
  'university.svg',
  'college.svg',
  'bank.svg',
] as const;

/** Human-readable label for each marker icon (indexed by position in CANVAS_MARKER_ICON_FILES). */
export const CANVAS_MARKER_ICON_LABELS: Record<string, string> = {
  'marker_red.svg': 'Red Pin',
  'marker_green.svg': 'Green Pin',
  'marker_blue.svg': 'Blue Pin',
  'marker_orange.svg': 'Orange Pin',
  'marker_purple.svg': 'Purple Pin',
  'plane.svg': 'Airport',
  'hospital.svg': 'Hospital',
  'fire.svg': 'Fire Station',
  'train_1.svg': 'Train (Front)',
  'train_2.svg': 'Train (Side)',
  'school.svg': 'School',
  'university.svg': 'University',
  'college.svg': 'College',
  'bank.svg': 'Bank / ATM',
};

/** Logical pixel size of marker SVG assets (`viewBox="0 0 24 36"`). */
export const CANVAS_MARKER_ICON_WIDTH = 24;
export const CANVAS_MARKER_ICON_HEIGHT = 36;
/** Target on-map marker width in screen pixels. */
export const CANVAS_MARKER_DISPLAY_WIDTH = 25;

const markerStyleCache = new Map<string, Style>();

const FILL_ALPHA_SUFFIX = '44';

function canvasSurfaceFillWithAlpha(): string {
  const surface = getCanvasColor(CANVAS_COLOR_VARS.surface);
  const hex = surface.startsWith('#') ? surface.slice(1, 7) : surface;
  return `#${hex}${FILL_ALPHA_SUFFIX}`;
}

/** Random hex color (ui-canvas `getRandomColor`). */
export function getRandomLayerColor(): string {
  let num = '';
  for (let i = 0; i < 6; i++) {
    num += Math.floor(Math.random() * 11).toString(16);
  }
  return `#${num}`;
}

export function markerIconUrl(iconsBaseUrl: string, iconId: number): string {
  const base = iconsBaseUrl.replace(/\/+$/, '');
  const file =
    CANVAS_MARKER_ICON_FILES[iconId] ?? CANVAS_MARKER_ICON_FILES[0];
  return `${base}/marker/${file}`;
}

/** OpenLayers style for a canvas marker icon index. */
export function markerIconStyle(iconsBaseUrl: string, iconId: number): Style {
  const base = iconsBaseUrl.replace(/\/+$/, '');
  const cacheKey = `${base}:${iconId}`;
  const cached = markerStyleCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const style = new Style({
    image: new Icon({
      anchor: [0.5, 0.85],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      src: markerIconUrl(iconsBaseUrl, iconId),
      size: [CANVAS_MARKER_ICON_WIDTH, CANVAS_MARKER_ICON_HEIGHT],
      width: CANVAS_MARKER_DISPLAY_WIDTH,
    }),
  });
  markerStyleCache.set(cacheKey, style);
  return style;
}

/** Polygon/line style from layer color (ui-canvas `styleFunction`). */
export function vectorColorStyle(color: string, feature: FeatureLike): Style {
  const geom = feature.getGeometry();
  const type = geom?.getType();

  if (type === 'LineString' || type === 'MultiLineString') {
    return new Style({
      stroke: new Stroke({ color, width: 2 }),
    });
  }

  if (
    type === 'Circle' ||
    type === 'Polygon' ||
    type === 'MultiPolygon'
  ) {
    return new Style({
      fill: new Fill({ color: `${color}${FILL_ALPHA_SUFFIX}` }),
      stroke: new Stroke({ color, width: 2 }),
    });
  }

  return new Style({
    stroke: new Stroke({ color, width: 2 }),
    fill: new Fill({ color: canvasSurfaceFillWithAlpha() }),
  });
}

export function isPointGeometry(feature: FeatureLike): boolean {
  const g = feature.getGeometry();
  return g instanceof Point || g instanceof MultiPoint;
}

export function isAreaGeometry(feature: FeatureLike): boolean {
  const g = feature.getGeometry();
  return (
    g instanceof Polygon ||
    g instanceof MultiPolygon ||
    g instanceof Circle ||
    g instanceof LineString ||
    g instanceof MultiLineString
  );
}

/** Dashed line + vertex dots while using the measure tool (ui-canvas `measurementStyle`). */
export function measurementDrawStyle(): Style {
  const strokeColor = getCanvasColor(CANVAS_COLOR_VARS.measureStroke);
  return new Style({
    stroke: new Stroke({
      width: 2,
      lineDash: [10, 10],
      color: strokeColor,
    }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.measureVertexFill) }),
      stroke: new Stroke({ width: 1, color: getCanvasColor(CANVAS_COLOR_VARS.measureVertexStroke) }),
    }),
  });
}

/** Human-readable length label for the live measure tooltip. */
export function formatMeasureLength(
  line: LineString,
  projection: Projection,
): string {
  const length = getLength(line, { projection });
  if (length > 1000) {
    return `${Math.round(length / 10) / 100} km`;
  }
  return `${Math.round(length * 100) / 100} m`;
}
